import { CanadaPostManifestMode } from "@redotech/redo-model/carrier-account/canada-post";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { type Validator } from "@redotech/ui/validator";
import { nonEmptyValidator } from "@redotech/ui/validators";
import { memo, useMemo, useState } from "react";
import { RedoTextInput } from "../../../../arbiter-components/input/redo-text-input";
import { RedoListItem } from "../../../../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "../../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "../../../../flex";

const canadaPostApiKeyValidator: Validator<string> = (value) => {
  const requiredErrors = nonEmptyValidator(value);
  if (requiredErrors.length > 0) {
    return requiredErrors;
  }

  const apiKey = value.trim();
  const apiKeySeparatorIndex = apiKey.indexOf(":");
  if (apiKeySeparatorIndex <= 0 || apiKeySeparatorIndex === apiKey.length - 1) {
    return ["Enter the API key in username:password format"];
  }

  return [];
};

export const CanadaPostDirectCarrierInput = groupInput({
  apiKey: input<string>({ validator: canadaPostApiKeyValidator }),
  customerNumber: input<string>({ validator: nonEmptyValidator }),
  contractNumber: input<string>({ validator: nonEmptyValidator }),
  manifestMode: input<CanadaPostManifestMode>({ validator: nonEmptyValidator }),
});

export type CanadaPostDirectCarrierValue = InputSchema.Value<
  typeof CanadaPostDirectCarrierInput
>;

export type CanadaPostDirectCarrierForm = InputSchema.Input<
  typeof CanadaPostDirectCarrierInput
>;

export const canadaPostDirectCarrierDefault =
  (): CanadaPostDirectCarrierValue => ({
    apiKey: "",
    customerNumber: "",
    contractNumber: "",
    manifestMode: CanadaPostManifestMode.ManifestNotRequired,
  });

const manifestModeOptions: RedoListItem<CanadaPostManifestMode>[] = [
  {
    id: CanadaPostManifestMode.ManifestNotRequired,
    type: "text",
    text: "Manifest not required",
    value: CanadaPostManifestMode.ManifestNotRequired,
  },
  {
    id: CanadaPostManifestMode.ManifestRequired,
    type: "text",
    text: "Manifest required",
    value: CanadaPostManifestMode.ManifestRequired,
  },
];

export const CreateCanadaPostDirectCarrierForm = memo(
  function CreateCanadaPostDirectCarrierForm({
    carrierInput,
  }: {
    carrierInput: CanadaPostDirectCarrierForm;
  }) {
    const [manifestModeSearch, setManifestModeSearch] = useState("");
    const filteredManifestModeOptions = useMemo(
      () =>
        manifestModeOptions.filter((option) =>
          option.value.toLowerCase().includes(manifestModeSearch.toLowerCase()),
        ),
      [manifestModeSearch],
    );

    return (
      <Flex dir="column">
        <RedoTextInput
          label="API key"
          placeholder="username:password"
          setValue={carrierInput.inputs.apiKey.setValue}
          value={carrierInput.inputs.apiKey.value}
        />
        <RedoTextInput
          label="Customer number"
          placeholder=""
          setValue={carrierInput.inputs.customerNumber.setValue}
          value={carrierInput.inputs.customerNumber.value}
        />
        <RedoTextInput
          label="Contract ID"
          placeholder=""
          setValue={carrierInput.inputs.contractNumber.setValue}
          value={carrierInput.inputs.contractNumber.value}
        />
        <RedoSingleSelectDropdownInput
          label="Manifest mode"
          options={filteredManifestModeOptions}
          optionSelected={({
            item,
          }: {
            item: RedoListItem<CanadaPostManifestMode>;
          }) => {
            carrierInput.inputs.manifestMode.setValue(item.value);
          }}
          searchPlaceholder="Search manifest modes..."
          searchString={manifestModeSearch}
          selectedItem={manifestModeOptions.find(
            (option) => option.value === carrierInput.inputs.manifestMode.value,
          )}
          setSearchString={setManifestModeSearch}
        />
      </Flex>
    );
  },
);
